/**
 * A file field that uploads first (`x-cis-field.upload`: the supplier's quote document): what the shell may refuse
 * before sending, and the ref an upload route answers. The route still judges the bytes; this only saves a person
 * a round trip for a file that is plainly the wrong kind or too big.
 */
import { formatBytes } from "../formatters";
import type { UploadTarget } from "./contract";

/** The words for a media type a field accepts ("PDF"); anything unnamed reads as itself. */
const MEDIA_TYPE_NAMES: Record<string, string> = {
  "application/pdf": "PDF",
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "text/csv": "CSV",
  "message/rfc822": "email (.eml)",
  "application/vnd.ms-outlook": "Outlook email (.msg)",
};

export function mediaTypeName(mediaType: string): string {
  return MEDIA_TYPE_NAMES[mediaType] ?? mediaType;
}

/** "PDF", "PDF or PNG", "PDF, PNG or JPEG". */
function joinWithOr(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/**
 * Why this file is not sent, or null to send it: a type the field does not accept (when the browser knows the
 * type), or a size over its limit.
 */
export function uploadRefusal(
  file: { name: string; size: number; type: string },
  accept: readonly string[] | null,
  maxBytes: number | null,
): string | null {
  const mediaTypes = (accept ?? []).filter((entry) => !entry.startsWith("."));
  const extensions = (accept ?? []).filter((entry) => entry.startsWith("."));
  // Browsers seldom type a saved email, so an accepted extension is enough.
  const hasAcceptedExtension = extensions.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  );
  if (
    mediaTypes.length > 0 &&
    file.type &&
    !hasAcceptedExtension &&
    !mediaTypes.includes(file.type)
  )
    return `${file.name} is not a ${joinWithOr(mediaTypes.map(mediaTypeName))} file.`;
  if (maxBytes !== null && file.size > maxBytes)
    return `${file.name} is ${formatBytes(file.size)}, over the ${formatBytes(maxBytes)} limit.`;
  return null;
}

/**
 * Whether a field accepting `accept` takes a pasted image of this media type: it names no kinds (any file), or
 * names this one.
 */
export function acceptsPastedImage(
  accept: readonly string[] | null,
  mediaType: string,
): boolean {
  const mediaTypes = (accept ?? []).filter((entry) => !entry.startsWith("."));
  return mediaTypes.length === 0 || mediaTypes.includes(mediaType);
}

const EXTENSION_OF_PASTED_IMAGE: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};

/**
 * The name given to an image pasted into a file field: "Pasted screenshot 2026-10-27 14.03.22.png", in the
 * person's own time, with no colons so any system keeps it as a file name.
 */
export function pastedScreenshotName(at: Date, mediaType: string): string {
  const two = (n: number) => String(n).padStart(2, "0");
  const date = `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}`;
  const time = `${two(at.getHours())}.${two(at.getMinutes())}.${two(at.getSeconds())}`;
  const extension = EXTENSION_OF_PASTED_IMAGE[mediaType] ?? "png";
  return `Pasted screenshot ${date} ${time}.${extension}`;
}

/** The first image among a paste's files, or null when it carries none (text, or a non-image file). */
export function firstPastedImage(files: ArrayLike<Blob>): Blob | null {
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    if (file && file.type.startsWith("image/")) return file;
  }
  return null;
}

/** Why this many files cannot be added to a several-files field, or null when they fit. */
export function tooManyFilesRefusal(
  kept: number,
  adding: number,
  maxFiles: number | null,
): string | null {
  if (maxFiles === null || kept + adding <= maxFiles) return null;
  return `You can add up to ${maxFiles} ${maxFiles === 1 ? "file" : "files"}.`;
}

/** The ref an upload route answered, under the field's `ref_field`; throws when it answered none. */
export function refOf(answer: unknown, target: UploadTarget): string {
  const ref =
    answer !== null && typeof answer === "object"
      ? (answer as Record<string, unknown>)[target.ref_field]
      : undefined;
  if (typeof ref !== "string" || ref === "")
    throw new Error(
      `The upload at ${target.path} answered no ${target.ref_field}.`,
    );
  return ref;
}

/** The refs a several-files field's uploads answered, in order. */
export function refsOf(
  answers: readonly unknown[],
  target: UploadTarget,
): string[] {
  return answers.map((answer) => refOf(answer, target));
}

/**
 * One file of a several-files field, in the order chosen. A kept file holds the ref its upload answered; an
 * uploading one is waiting for the route (one file at a time); a refused one is the shell's refusal before sending
 * (`retryable: false`) or the route's own (`retryable: true`, with Try again).
 */
export type FileEntry =
  | {
      id: string;
      status: "kept";
      name: string;
      size: number | null;
      ref: string;
    }
  | { id: string; status: "uploading"; name: string }
  | {
      id: string;
      status: "refused";
      name: string;
      message: string;
      retryable: boolean;
    };

/** What a several-files field holds: the refs of the kept files, in the order chosen. */
export function keptRefsOf(entries: readonly FileEntry[]): string[] {
  return entries.flatMap((entry) =>
    entry.status === "kept" ? [entry.ref] : [],
  );
}

/** The entries with one replaced where it stands (a retry keeps its place); unchanged if it was removed meanwhile. */
export function settleEntry(
  entries: readonly FileEntry[],
  next: FileEntry,
): FileEntry[] {
  return entries.map((entry) => (entry.id === next.id ? next : entry));
}

export function withoutEntry(
  entries: readonly FileEntry[],
  id: string,
): FileEntry[] {
  return entries.filter((entry) => entry.id !== id);
}

/**
 * The entries for the refs a several-files field is given (a prefill, or the form re-reading its body): a ref the
 * field already knows keeps its name and size, any other is "File uploaded" (a ref carries no name).
 */
export function entriesFromRefs(
  refs: readonly string[],
  known: readonly FileEntry[],
): FileEntry[] {
  return refs.map((ref, i) => {
    const same = known.find(
      (entry) => entry.status === "kept" && entry.ref === ref,
    );
    return (
      same ?? {
        id: `ref-${i}-${ref}`,
        status: "kept",
        name: "File uploaded",
        size: null,
        ref,
      }
    );
  });
}

/** The files that count toward a field's limit: every one except those refused. */
export function countedFiles(entries: readonly FileEntry[]): number {
  return entries.filter((entry) => entry.status !== "refused").length;
}
